import type { MeetingSocketConnectParams, MeetingSocketPort } from '../ports/MeetingSocketPort';
import type { HostActionKind, MeetingRoomEvent, MeetingSocketConnectionState, MeetingStatus } from '@shared/types';

interface WsMeetingSocketOptions {
  /** e.g. https://api.example.com — the http(s) base of the backend, converted to wss(s) internally. */
  baseUrl: string;
  /** Milliseconds to wait before reconnect attempts after an unexpected close. Set 0 to disable. */
  reconnectDelayMs?: number;
}

/**
 * Native WebSocket-backed implementation of MeetingSocketPort, matching
 * GET /meetings/{id}/ws (see websocket-events.md).
 *
 * Responsibility is intentionally narrow: open/close the socket, parse and
 * forward JSON events, and reconnect on unexpected drops. It does not
 * interpret event *meaning* (e.g. updating a participants list) — that is
 * composed on top by a store, same pattern as RoomService/StreamPublisherPort.
 */
export class WsMeetingSocket implements MeetingSocketPort {
  private socket: WebSocket | null = null;
  private state: MeetingSocketConnectionState = 'idle';
  private reconnectTimer: number | null = null;
  private lastConnectParams: MeetingSocketConnectParams | null = null;
  private explicitDisconnect = false;
  private reconnectAttempts = 0;
  private static readonly MAX_RECONNECT_ATTEMPTS = 5;

  private readonly stateListeners = new Set<(state: MeetingSocketConnectionState) => void>();
  private readonly eventListeners = new Set<(event: MeetingRoomEvent) => void>();
  /** Fired when reconnect attempts are exhausted — caller (roomStore) should
   *  fetch a fresh ws_ticket (re-join) or surface a hard error to the user. */
  private readonly exhaustedListeners = new Set<() => void>();

  constructor(private readonly options: WsMeetingSocketOptions) { }

  connect(params: MeetingSocketConnectParams): void {
    this.explicitDisconnect = false;
    this.reconnectAttempts = 0;
    this.lastConnectParams = params;
    this.openSocket(params);
  }

  disconnect(): void {
    this.explicitDisconnect = true;
    this.clearReconnectTimer();
    this.socket?.close(1000, 'client-disconnect');
    this.socket = null;
    this.setState('closed');
  }

  getState(): MeetingSocketConnectionState {
    return this.state;
  }

  onStateChanged(listener: (state: MeetingSocketConnectionState) => void): () => void {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => this.stateListeners.delete(listener);
  }

  onEvent(listener: (event: MeetingRoomEvent) => void): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  onReconnectExhausted(listener: () => void): () => void {
    this.exhaustedListeners.add(listener);
    return () => this.exhaustedListeners.delete(listener);
  }

  private openSocket(params: MeetingSocketConnectParams): void {
    this.setState('connecting');

    const url = this.buildUrl(params);
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.reconnectAttempts = 0;
      this.setState('open');
    });

    socket.addEventListener('message', (evt) => {
      const parsed = this.parseEvent(evt.data);
      if (parsed) {
        this.emitEvent(parsed);
      }
    });

    socket.addEventListener('close', () => {
      if (this.socket !== socket) {
        return; // stale socket, already superseded
      }
      this.socket = null;
      this.setState('closed');
      if (!this.explicitDisconnect) {
        this.scheduleReconnect();
      }
    });

    socket.addEventListener('error', () => {
      this.setState('error');
    });
  }

  private scheduleReconnect(): void {
    const baseDelay = this.options.reconnectDelayMs ?? 2000;
    if (baseDelay <= 0 || !this.lastConnectParams) {
      return;
    }
    if (this.reconnectAttempts >= WsMeetingSocket.MAX_RECONNECT_ATTEMPTS) {
      for (const listener of this.exhaustedListeners) {
        listener();
      }
      return;
    }
    this.reconnectAttempts += 1;
    // экспоненциальный backoff с потолком 15с: 2s, 4s, 8s, 15s, 15s...
    const delay = Math.min(baseDelay * 2 ** (this.reconnectAttempts - 1), 15000);
    this.clearReconnectTimer();
    this.reconnectTimer = window.setTimeout(() => {
      if (!this.explicitDisconnect && this.lastConnectParams) {
        this.openSocket(this.lastConnectParams);
      }
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private buildUrl(params: MeetingSocketConnectParams): string {
    const httpBase = this.options.baseUrl.replace(/\/$/, '');
    const wsBase = httpBase.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
    const query = new URLSearchParams({
      token: params.wsTicket,
      participant_id: params.participantId,
    });
    return `${wsBase}/meetings/${params.meetingId}/ws?${query.toString()}`;
  }

  private parseEvent(raw: unknown): MeetingRoomEvent | null {
    if (typeof raw !== 'string') {
      return null;
    }
    try {
      const data = JSON.parse(raw) as { type?: string } & Record<string, unknown>;
      if (!data || typeof data.type !== 'string') {
        return null;
      }
      return this.mapEvent(data as { type: string } & Record<string, unknown>);
    } catch {
      return null;
    }
  }

  private mapEvent(data: { type: string } & Record<string, unknown>): MeetingRoomEvent | null {
    switch (data.type) {
      case 'ParticipantJoined':
        return {
          type: 'ParticipantJoined',
          participantId: String(data.participant_id),
          displayName: String(data.display_name),
        };
      case 'ParticipantLeft':
        return { type: 'ParticipantLeft', participantId: String(data.participant_id) };
      case 'ParticipantApproved':
        return { type: 'ParticipantApproved', participantId: String(data.participant_id) };
      case 'MediaTokenIssued':
        return { type: 'MediaTokenIssued', token: String(data.token) };
      case 'ParticipantDenied':
        return { type: 'ParticipantDenied', participantId: String(data.participant_id) };
      case 'HostAction': {
        const action = data.action as HostActionKind;
        const targetId = typeof data.target_id === 'string' ? data.target_id : undefined;
        return { type: 'HostAction', action, targetId };
      }
      case 'MeetingStatusChanged': {
        const status = data.status as MeetingStatus;
        return { type: 'MeetingStatusChanged', status };
      }
      default:
        return null;
    }
  }

  private setState(state: MeetingSocketConnectionState): void {
    this.state = state;
    for (const listener of this.stateListeners) {
      listener(state);
    }
  }

  private emitEvent(event: MeetingRoomEvent): void {
    for (const listener of this.eventListeners) {
      listener(event);
    }
  }
}